// src/components/MenuItem.js

import React, { useState } from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  Box,
  IconButton,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { Add, Remove, Delete } from '@mui/icons-material';
import { useCart } from '../CartContext';

// dietary icons
import GFIcon from '../assets/gluten_free.png';
import VTIcon from '../assets/Vegetarian.png';
import VGIcon from '../assets/vegan.png';

export default function MenuItem({ item }) {
  const {
    addToCart,
    updateQuantity,
    removeFromCart,
    getItemQuantity
  } = useCart();

  // local state for GF up‐charge
  const [gfChecked, setGfChecked] = useState(false);

  // quantity is keyed by item name + GF flag
  const qty = getItemQuantity(item.name, gfChecked);

  // calculate per‐unit price including upcharge
  const upcharge = item.dietary.glutenFreeOption && gfChecked ? 1 : 0;
  const unitPrice = item.price + upcharge;

  const handleAdd = () =>
    addToCart(item, { glutenFreeOption: gfChecked });

  const inc = () =>
    updateQuantity(item.name, qty + 1, gfChecked);

  const dec = () =>
    updateQuantity(item.name, qty - 1, gfChecked);

  const rem = () =>
    removeFromCart(item.name, gfChecked);

  return (
    <Card
      sx={{
        width: '100%',
        maxWidth: { xs: 340, sm: 280 },
        display: 'flex',
        flexDirection: 'column',
        height: 460,
        boxShadow: 3,
        borderRadius: 2,
        overflow: 'hidden'
      }}
    >
      {/* image */}
      <CardMedia
        component="img"
        image={item.image}
        alt={item.name}
        sx={{ height: 180, objectFit: 'cover' }}
      />

      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        {/* name */}
        <Typography variant="h6" gutterBottom>
          {item.name}
        </Typography>

        {/* dietary icons */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {item.dietary.glutenFree && (
            <Box
              component="img"
              src={GFIcon}
              alt="Gluten Free"
              sx={{ width: 20, height: 20, mr: 1 }}
            />
          )}
          {item.dietary.vegetarian && (
            <Box
              component="img"
              src={VTIcon}
              alt="Vegetarian"
              sx={{ width: 20, height: 20, mr: 1 }}
            />
          )}
          {item.dietary.vegan && (
            <Box
              component="img"
              src={VGIcon}
              alt="Vegan"
              sx={{ width: 20, height: 20, mr: 1 }}
            />
          )}
        </Box>

        {/* Gluten-free upcharge checkbox on breakfast sandwiches */}
        {item.mealType === 'Breakfast' &&
          item.subCategory === 'Sandwiches' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={gfChecked}
                  onChange={e => setGfChecked(e.target.checked)}
                />
              }
              label="Gluten Free Option (+$1)"
              sx={{ mb: 1 }}
            />
        )}

        {/* ingredients */}
        {item.ingredients && (
          <Typography variant="body2" color="text.secondary">
            Ingredients: {item.ingredients}
          </Typography>
        )}
      </CardContent>

      {/* add / quantity controls */}
      <Box sx={{ p: 2 }}>
        {qty === 0 ? (
          <Button
            variant="contained"
            fullWidth
            onClick={handleAdd}
            sx={{
              bgcolor: 'black',
              color: 'white',
              '&:hover': { bgcolor: '#333' }
            }}
          >
            ${unitPrice.toFixed(2)} – Add
          </Button>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <IconButton onClick={dec}>
              <Remove />
            </IconButton>
            <Typography>{qty}</Typography>
            <IconButton onClick={inc}>
              <Add />
            </IconButton>
            <IconButton onClick={rem} color="error">
              <Delete />
            </IconButton>
          </Box>
        )}
      </Box>
    </Card>
  );
}
